import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BookOpen, Brain, Sparkles, Trophy, Users, Zap, ArrowRight,
  Loader2, Search, Upload, GraduationCap, Gift, DollarSign,
  CheckCircle2, Star, Globe, FileText, Wand2, TrendingUp, Lock,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import NoteCard from "@/components/NoteCard";
import { useTrendingNotes } from "@/hooks/useNotes";
import { useCourses } from "@/hooks/useCourses";
import heroPattern from "@/assets/hero-pattern-1536.webp";
import heroPatternSmall from "@/assets/hero-pattern-960.webp";
import heroPatternLarge from "@/assets/hero-pattern-1920.webp";
import ReferralBanner from "@/components/ReferralBanner";
import { useAuth } from "@/contexts/AuthContext";
import { SEO } from "@/components/SEO";

/* ─── features (reordered for conversion) ─── */
const features = [
  {
    icon: BookOpen,
    title: "Buy & Sell Notes",
    description: "Marketplace for high-quality student notes across every course and exam board.",
    link: "/browse",
  },
  {
    icon: Brain,
    title: "AI Study Tools",
    description: "Instantly generate flashcards, quizzes, and practice exams from any notes.",
    link: "/ai-tools",
  },
  {
    icon: DollarSign,
    title: "Earn Money",
    description: "Upload your notes, set your price, and earn every time someone downloads.",
    link: "/upload",
  },
  {
    icon: Users,
    title: "Study Groups",
    description: "Join course-based communities and collaborate with classmates.",
    link: "/my-groups",
  },
  {
    icon: Trophy,
    title: "Leaderboards",
    description: "Compete for top creator status and discover the best study materials.",
    link: "/leaderboard",
  },
  {
    icon: Zap,
    title: "Smart Search",
    description: "Find exactly what you need with filters for course code, exam board, and more.",
    link: "/browse",
  },
];

/* ─── how-it-works steps ─── */
const steps = [
  { num: 1, icon: Upload, title: "Upload your notes", desc: "Share your study materials in seconds — PDFs, docs, anything." },
  { num: 2, icon: Wand2, title: "Turn them into AI tools", desc: "Auto-generate flashcards, quizzes, summaries, and practice exams." },
  { num: 3, icon: TrendingUp, title: "Earn money and study smarter", desc: "Set your price and earn, or use AI tools to ace your exams." },
];

/* ─── sub-components ─── */

function HeroSearchBar() {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();
  const shouldLoadCourses = focused && query.trim().length >= 2;
  const { data: courses = [] } = useCourses({ enabled: shouldLoadCourses });

  const suggestions = useMemo(() => {
    if (!query.trim() || query.length < 2) return [];
    const q = query.toLowerCase();
    return courses
      .filter(
        (c) =>
          c.course_code.toLowerCase().includes(q) ||
          c.subject.toLowerCase().includes(q) ||
          c.education_level.toLowerCase().includes(q)
      )
      .slice(0, 5);
  }, [query, courses]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) navigate(`/browse?search=${encodeURIComponent(query.trim())}`);
  };

  const handleSelectCourse = (course: (typeof courses)[0]) => {
    navigate(`/course/${encodeURIComponent(course.education_level)}/${encodeURIComponent(course.course_code)}`);
  };

  return (
    <form onSubmit={handleSearch} className="relative mx-auto mt-8 max-w-2xl">
      <div className="relative flex items-center rounded-2xl border-2 border-primary/20 bg-card shadow-lg transition-all focus-within:border-primary/50 focus-within:shadow-xl">
        <Search className="ml-5 h-5 w-5 text-muted-foreground shrink-0" />
        <Input
          type="text"
          aria-label="Search study notes, courses, or subjects"
          placeholder="Search notes, courses, subjects…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 200)}
          className="border-0 bg-transparent py-7 pl-3 pr-4 text-base shadow-none focus-visible:ring-0 placeholder:text-muted-foreground/60"
        />
        <Button type="submit" variant="hero" size="lg" className="mr-2 shrink-0 rounded-xl px-6">
          Search
        </Button>
      </div>

      {focused && suggestions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute left-0 right-0 top-full z-50 mt-2 rounded-xl border border-border bg-card shadow-xl overflow-hidden"
        >
          {suggestions.map((course) => (
            <button
              key={`${course.education_level}::${course.course_code}`}
              type="button"
              onMouseDown={() => handleSelectCourse(course)}
              className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors border-b border-border/50 last:border-0"
            >
              <GraduationCap className="h-4 w-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-foreground">{course.subject}</span>
                <span className="text-xs text-muted-foreground ml-2">{course.education_level} · {course.course_code}</span>
              </div>
              <span className="text-xs text-muted-foreground shrink-0">
                {course.note_count} {course.note_count === 1 ? "note" : "notes"}
              </span>
            </button>
          ))}
        </motion.div>
      )}
    </form>
  );
}

function TrendingNotes() {
  const [enabled, setEnabled] = useState(false);
  const { data: notes = [], isLoading } = useTrendingNotes({ enabled });
  const { user } = useAuth();

  useEffect(() => {
    const id = window.setTimeout(() => setEnabled(true), 1200);
    return () => window.clearTimeout(id);
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (notes.length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        No notes uploaded yet. Be the first to share your study materials!
      </div>
    );
  }

  return (
    <div className="relative">
      <div className={`grid gap-4 sm:grid-cols-2 lg:grid-cols-3 ${!user ? "max-h-[420px] overflow-hidden" : ""}`}>
        {notes.map((note, i) => (
          <motion.div
            key={note.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
          >
            <NoteCard note={note} />
          </motion.div>
        ))}
      </div>

      {/* Blur overlay for non-logged-in users */}
      {!user && (
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-10 bg-gradient-to-t from-background via-background/90 to-transparent rounded-xl">
          <Lock className="h-8 w-8 text-primary mb-3" />
          <p className="text-lg font-semibold text-foreground mb-1">Sign up to access full notes</p>
          <p className="text-sm text-muted-foreground mb-4">Create a free account to browse and purchase notes</p>
          <Button variant="hero" size="lg" asChild>
            <Link to="/auth?mode=signup">
              Unlock Notes <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}

function SocialProofBar() {
  return (
    <section className="border-y border-border bg-muted/30">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14 text-center">
          <div className="flex flex-col items-center gap-1">
            <div className="flex gap-0.5 text-accent">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-current" />
              ))}
            </div>
            <span className="text-xs text-muted-foreground">Loved by students</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Globe className="h-4 w-4 text-primary" />
            <span>Used by students in <strong className="text-foreground">10+ countries</strong></span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileText className="h-4 w-4 text-primary" />
            <span>Notes for <strong className="text-foreground">all subjects</strong></span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <BookOpen className="h-4 w-4 text-primary" />
            <span>Notes for <strong className="text-foreground">all levels of education</strong></span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── main page ─── */

export default function Landing() {
  const navigate = useNavigate();
  const { user, subscription } = useAuth();

  return (
    <div className="min-h-screen">
      <SEO
        title="GatorNotes — Student Study Notes Marketplace & AI Tutor"
        description="Find study notes for IGCSE, A-Level, IB, and university courses. Every note includes AI flashcards, quizzes, and tutoring to help you revise."
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "GatorNotes",
            url: "https://gatornotes.org",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://gatornotes.org/browse?search={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What is GatorNotes?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "GatorNotes is an AI-powered marketplace where students buy and sell study notes for IGCSE, GCSE, A-Level, IB, AP, and university courses. Every note comes with AI-generated flashcards, practice quizzes, and an AI tutor.",
                },
              },
              {
                "@type": "Question",
                name: "Is GatorNotes free to use?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes — signup is free and free notes are available. Paid notes start at €0.50. GatorNotes Plus (€4.99/mo) adds 15% off every purchase, visibility boosts, and unlimited premium AI tutoring.",
                },
              },
              {
                "@type": "Question",
                name: "How do I sell my study notes?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Create a free account, upload your PDF on the Sell page, set a price between €0.50 and €50, and keep 78% of every sale. Payouts are sent in EUR to PayPal or bank above the €5 minimum.",
                },
              },
              {
                "@type": "Question",
                name: "How does the AI tutor work?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Our AI tutor reads each note's content and answers your questions, generates summaries, flashcards, and timed quizzes — all grounded in the actual PDF you bought, so answers stay on-syllabus.",
                },
              },
            ],
          },
        ]}
      />

      {/* Referral Banner */}
      <ReferralBanner />

      {/* ═══ HERO ═══ */}
      <section className="relative overflow-hidden">
        <img
          src={heroPattern}
          srcSet={`${heroPatternSmall} 960w, ${heroPattern} 1536w, ${heroPatternLarge} 1920w`}
          sizes="100vw"
          width={1536}
          height={1024}
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-x-0 -top-[12%] h-[112%] w-full object-cover opacity-[0.07] pointer-events-none select-none"
        />
        <div className="container mx-auto px-4 pt-2 pb-8 sm:pt-3 sm:pb-10 md:pt-3 md:pb-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mx-auto max-w-3xl text-center"
          >
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-4 py-1.5 text-sm text-accent font-semibold">
              <DollarSign className="h-4 w-4" />
              Earn extra money from your study notes
            </div>

            <h1 className="font-display text-4xl font-bold leading-tight tracking-tight md:text-6xl lg:text-7xl">
              Sell your study notes online.{" "}
              <span className="gradient-text">Earn money while you sleep.</span>
            </h1>

            <p className="mx-auto mt-6 max-w-xl text-lg text-muted-foreground">
              The dedicated marketplace to <Link to="/sell" className="underline decoration-primary/40 underline-offset-4 hover:text-foreground">sell study notes online</Link> for IGCSE, A-Level, IB, AP & university.
              Or <Link to="/browse" className="underline decoration-primary/40 underline-offset-4 hover:text-foreground">buy revision notes</Link> + AI flashcards, quizzes & tutoring.
            </p>

            {/* CTA Buttons */}
            <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Button variant="hero" size="xl" asChild>
                <Link to={user ? "/upload" : "/auth?mode=signup&from=sell"}>
                  <DollarSign className="mr-2 h-5 w-5" />
                  Start Earning — It's Free
                </Link>
              </Button>
              <Button variant="hero-outline" size="xl" asChild>
                <Link to="/browse">
                  <BookOpen className="mr-2 h-5 w-5" /> Browse Study Notes
                </Link>
              </Button>
            </div>

            {/* Trust bullets */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-primary" /> Free to list
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-primary" /> Set your own price
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-primary" /> Fast payouts
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-primary" /> No credit card needed
              </span>
            </div>

            {/* Search */}
            <HeroSearchBar />

            {/* Subtle links */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 text-xs text-muted-foreground">
              <Link to="/sell" className="hover:text-foreground transition-colors flex items-center gap-1">
                <DollarSign className="h-3 w-3" /> How selling works & FAQ
              </Link>
              <span className="hidden sm:inline">·</span>
              <Link to="/referrals" className="hover:text-foreground transition-colors flex items-center gap-1">
                <Gift className="h-3 w-3" /> Earn free Plus with referrals
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ SOCIAL PROOF ═══ */}
      <SocialProofBar />

      {/* ═══ HOW IT WORKS ═══ */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-2xl text-center mb-14">
            <h2 className="font-display text-3xl font-bold text-foreground md:text-4xl">
              How GatorNotes Works
            </h2>
            <p className="mt-4 text-muted-foreground">
              Three simple steps to start earning and studying smarter.
            </p>
          </div>

          <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-3">
            {steps.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="relative text-center"
              >
                {/* connector line */}
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[60%] w-[80%] border-t-2 border-dashed border-primary/20" />
                )}
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 ring-4 ring-primary/5">
                  <step.icon className="h-7 w-7 text-primary" />
                </div>
                <span className="inline-block mb-2 rounded-full bg-primary/10 px-3 py-0.5 text-xs font-semibold text-primary">
                  Step {step.num}
                </span>
                <h3 className="font-display text-lg font-semibold text-foreground mb-1">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ EARN MONEY ═══ */}
      <section className="surface-sunken py-20 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl rounded-2xl border border-primary/20 bg-card p-8 md:p-14">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary mb-4">
                  <DollarSign className="h-4 w-4" /> Earn Money
                </div>
                <h2 className="font-display text-3xl font-bold text-foreground md:text-4xl mb-4">
                  Turn your notes into income
                </h2>
                <p className="text-muted-foreground mb-6">
                  Your study notes are valuable. Upload them, set your price, and earn every time a student downloads.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    "Upload your notes in seconds",
                    "Set your own price",
                    "Earn every time someone downloads",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-foreground">
                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button variant="hero" size="lg" asChild>
                  <Link to="/upload">
                    Start Earning <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="grid grid-cols-2 gap-4"
              >
                {[
                  { label: "Upload", icon: Upload, desc: "Drop your PDF or doc" },
                  { label: "Set Price", icon: DollarSign, desc: "You choose the price" },
                  { label: "Get Discovered", icon: Search, desc: "Students find your notes" },
                  { label: "Get Paid", icon: TrendingUp, desc: "Earn with every sale" },
                ].map((card) => (
                  <div key={card.label} className="rounded-xl border border-border bg-muted/30 p-4 text-center">
                    <card.icon className="mx-auto h-6 w-6 text-primary mb-2" />
                    <p className="text-sm font-semibold text-foreground">{card.label}</p>
                    <p className="text-xs text-muted-foreground mt-1">{card.desc}</p>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-2xl text-center mb-12">
            <h2 className="font-display text-3xl font-bold text-foreground md:text-4xl">
              Everything you need to ace your exams
            </h2>
            <p className="mt-4 text-muted-foreground">
              From finding the perfect notes to generating AI-powered study tools — all in one place.
            </p>
          </div>
          <div className="mx-auto grid max-w-5xl gap-4 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <Link key={f.title} to={f.link}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.04, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="glass-card-hover p-6 h-full cursor-pointer transition-shadow hover:shadow-glow"
                >
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <f.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-display font-semibold text-foreground mb-1">{f.title}</h3>
                  <p className="text-sm text-muted-foreground">{f.description}</p>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TRENDING NOTES ═══ */}
      <section className="surface-sunken py-20">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-display text-2xl font-bold text-foreground md:text-3xl">Trending Notes</h2>
              <p className="text-sm text-muted-foreground mt-1">Top-rated notes this week</p>
            </div>
            <Button variant="outline" asChild>
              <Link to="/browse">View all</Link>
            </Button>
          </div>
          <TrendingNotes />
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4">
          <div className="gradient-primary rounded-2xl p-10 md:p-16 text-center">
            <h2 className="font-display text-3xl font-bold text-primary-foreground md:text-4xl">
              Start earning and studying smarter today
            </h2>
            <p className="mt-4 text-primary-foreground/80 max-w-lg mx-auto">
              Join GatorNotes and unlock AI-powered studying + income from your notes.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button variant="secondary" size="xl" asChild>
                <Link to="/auth?mode=signup">
                  Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                size="xl"
                className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30 border-0"
                asChild
              >
                <Link to="/upload">
                  <Upload className="mr-2 h-5 w-5" /> Upload Notes
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
